import { useState, useCallback } from 'react';
import {
  Upload,
  FileText,
  BarChart3,
  CheckCircle,
  RefreshCw,
  Settings,
  DollarSign,
  AlertTriangle,
  Search,
  Layers,
  XCircle
} from 'lucide-react';

/**
 * 報表分析測試頁面
 * 
 * ⚠️ 測試報表相容性 - 不會影響正式系統
 * - 上傳報表後自動偵測格式
 * - 顯示偵測到的工作表、欄位映射
 * - 驗證解析結果是否正確
 */
export default function ReportAnalysis() {
  // ========== 模擬資料 ==========
  const [mockBudget, setMockBudget] = useState({
    initial_amount: 100000,
    remaining_amount: 100000,
    discount_rate: 0,
  });

  const [showBudgetForm, setShowBudgetForm] = useState(false);
  const [budgetForm, setBudgetForm] = useState({
    initial_amount: '100000',
    discount_rate: '',
  });

  // ========== 狀態 ==========
  const [file, setFile] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  
  // 解析結果
  const [result, setResult] = useState(null);
  const [message, setMessage] = useState({ type: '', text: '' });

  // ========== 預算設定 ==========
  const handleSetBudget = (e) => {
    e.preventDefault();
    const amount = parseFloat(budgetForm.initial_amount) || 100000;
    const discount = parseFloat(budgetForm.discount_rate) || 0;
    
    setMockBudget({
      initial_amount: amount,
      remaining_amount: amount,
      discount_rate: discount,
    });
    
    setShowBudgetForm(false);
    setMessage({ type: 'success', text: `✅ 預算設定: $${amount.toLocaleString()}, 折扣: ${discount > 0 ? (discount * 10).toFixed(1) + '折' : '無'}` });
  };

  // ========== 檔案處理 ==========
  const handleFileChange = useCallback((e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;

    const validTypes = [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-excel',
      'text/csv'
    ];
    
    if (!validTypes.includes(selectedFile.type) && !selectedFile.name.match(/\.(xlsx|xls|csv)$/i)) {
      setMessage({ type: 'error', text: '❌ 請上傳 Excel 或 CSV 檔案' });
      return;
    }

    setFile(selectedFile);
    setResult(null);
    setMessage({ type: 'info', text: `📄 已選擇: ${selectedFile.name}` });
  }, []);

  // ========== 分析報表 ==========
  const handleAnalyze = async () => {
    if (!file) {
      setMessage({ type: 'error', text: '❌ 請選擇檔案' });
      return;
    }

    setAnalyzing(true);
    setMessage({ type: 'info', text: '🔍 正在偵測報表格式...' });

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch('/api/expense/analyze-test', {
        method: 'POST',
        body: formData,
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        
        // 計算折扣後金額
        const discount = mockBudget.discount_rate;
        const amountWithDiscount = data.total_amount_with_discount || 0;
        const amountNoDiscount = data.total_amount_no_discount || 0;
        
        const discountedPart = discount > 0 && discount < 1 
          ? Math.round(amountWithDiscount * discount)
          : amountWithDiscount;
        
        const totalPayable = discountedPart + amountNoDiscount;
        
        setResult({
          ...data,
          calculated: {
            discount_rate: discount,
            discounted_part: discountedPart,
            total_payable: totalPayable,
          }
        });
        
        setMessage({ 
          type: 'success', 
          text: data.message || '解析完成'
        });
      } else {
        const errorData = await response.json();
        setMessage({ type: 'error', text: `❌ ${errorData.error || '分析失敗'}` });
      }
    } catch (error) {
      console.error('分析失敗:', error);
      setMessage({ type: 'error', text: `❌ 分析失敗: ${error.message}` });
    } finally {
      setAnalyzing(false);
    }
  };

  // ========== 重置 ==========
  const handleReset = () => {
    setFile(null);
    setResult(null);
    setMessage({ type: 'info', text: '🔄 已重置' });
  };

  // ========== 格式化 ==========
  const formatCurrency = (amount) => {
    if (amount == null) return 'N/A';
    return new Intl.NumberFormat('zh-TW', {
      style: 'currency',
      currency: 'TWD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const getReportTypeLabel = (type) => {
    const labels = {
      'azure_plan_daily': 'Azure Plan Daily Billing',
      'marketplace': 'Marketplace',
      'azure_cost': 'Azure Cost Report',
      'generic': '通用格式',
    };
    return labels[type] || type;
  };

  // ========== 渲染 ==========
  return (
    <div className="min-h-screen bg-gray-50 p-8">
      <div className="max-w-7xl mx-auto">
        {/* 標題 */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">🧪 報表相容性測試</h1>
          <p className="text-gray-600">上傳報表，測試系統能否正確偵測格式並解析</p>
          
          <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg flex items-start">
            <AlertTriangle className="w-5 h-5 text-yellow-600 mr-3 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-yellow-800 font-medium">測試模式</p>
              <p className="text-yellow-700 text-sm">
                此頁面僅用於測試，不會將資料寫入系統。
              </p>
            </div>
          </div>
        </div>

        {/* 訊息 */}
        {message.text && (
          <div className={`mb-6 p-4 rounded-lg ${
            message.type === 'error' ? 'bg-red-50 text-red-800' :
            message.type === 'success' ? 'bg-green-50 text-green-800' :
            'bg-blue-50 text-blue-800'
          }`}>
            {message.text}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* 左側：上傳與設定 */}
          <div className="space-y-6">
            {/* 上傳區域 */}
            <div className="bg-white rounded-lg shadow-lg p-6">
              <h2 className="text-lg font-semibold mb-4 flex items-center">
                <Upload className="w-5 h-5 mr-2 text-blue-600" />
                上傳報表
              </h2>

              <div className="space-y-4">
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-blue-400 transition-colors">
                  <input
                    type="file"
                    onChange={handleFileChange}
                    accept=".xlsx,.xls,.csv"
                    className="hidden"
                    id="file-upload"
                  />
                  <label htmlFor="file-upload" className="cursor-pointer">
                    <Upload className="w-10 h-10 mx-auto text-gray-400 mb-2" />
                    <p className="text-gray-600">點擊上傳 Excel</p>
                  </label>
                </div>
                
                {file && (
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <p className="text-sm text-gray-700 font-medium truncate">📄 {file.name}</p>
                    <p className="text-xs text-gray-500">{(file.size / 1024).toFixed(2)} KB</p>
                  </div>
                )}

                <button
                  onClick={handleAnalyze}
                  disabled={!file || analyzing}
                  className="w-full py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 flex items-center justify-center font-medium"
                >
                  {analyzing ? (
                    <>
                      <RefreshCw className="w-5 h-5 mr-2 animate-spin" />
                      偵測中...
                    </>
                  ) : (
                    <>
                      <Search className="w-5 h-5 mr-2" />
                      開始偵測
                    </>
                  )}
                </button>

                <button
                  onClick={handleReset}
                  className="w-full py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 flex items-center justify-center"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  重置
                </button>
              </div>
            </div>

            {/* 折扣設定 */}
            <div className="bg-white rounded-lg shadow-lg p-6">
              <h2 className="text-lg font-semibold mb-4 flex items-center">
                <Settings className="w-5 h-5 mr-2 text-gray-600" />
                模擬折扣
              </h2>
              
              <div className="p-3 bg-blue-50 rounded-lg mb-3">
                <p className="text-sm text-blue-800">
                  <span className="font-medium">目前折扣:</span>{' '}
                  {mockBudget.discount_rate > 0 && mockBudget.discount_rate < 1
                    ? `${(mockBudget.discount_rate * 10).toFixed(1)} 折`
                    : '無折扣'}
                </p>
              </div>
              
              <button
                onClick={() => setShowBudgetForm(!showBudgetForm)}
                className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
              >
                <DollarSign className="w-4 h-4 mr-1" />
                {showBudgetForm ? '收起' : '設定折扣'}
              </button>

              {showBudgetForm && (
                <form onSubmit={handleSetBudget} className="mt-3 p-3 bg-gray-50 rounded-lg space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">折扣率</label>
                    <input
                      type="number"
                      min="0"
                      max="1"
                      step="0.01"
                      value={budgetForm.discount_rate}
                      onChange={(e) => setBudgetForm({ ...budgetForm, discount_rate: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                      placeholder="0.85 = 8.5折"
                    />
                  </div>
                  <button
                    type="submit"
                    className="w-full py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
                  >
                    套用
                  </button>
                </form>
              )}
            </div>
          </div>

          {/* 右側：偵測結果 */}
          <div className="lg:col-span-3 space-y-6">
            {result && (
              <>
                {/* 偵測摘要 */}
                <div className="bg-white rounded-lg shadow-lg p-6">
                  <h2 className="text-lg font-semibold mb-4 flex items-center">
                    <Search className="w-5 h-5 mr-2 text-green-600" />
                    偵測結果
                  </h2>
                  
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                    <div className="p-3 bg-blue-50 rounded-lg text-center">
                      <p className="text-xs text-blue-600">偵測到工作表</p>
                      <p className="text-2xl font-bold text-blue-700">
                        {result.detection_summary?.total_sheets_found || 0}
                      </p>
                    </div>
                    <div className="p-3 bg-green-50 rounded-lg text-center">
                      <p className="text-xs text-green-600">套用折扣</p>
                      <p className="text-2xl font-bold text-green-700">
                        {result.detection_summary?.sheets_with_discount || 0}
                      </p>
                    </div>
                    <div className="p-3 bg-orange-50 rounded-lg text-center">
                      <p className="text-xs text-orange-600">不套用折扣</p>
                      <p className="text-2xl font-bold text-orange-700">
                        {result.detection_summary?.sheets_no_discount || 0}
                      </p>
                    </div>
                    <div className="p-3 bg-indigo-50 rounded-lg text-center">
                      <p className="text-xs text-indigo-600">總筆數</p>
                      <p className="text-2xl font-bold text-indigo-700">
                        {result.row_count || 0}
                      </p>
                    </div>
                  </div>

                  {/* 各工作表偵測結果 */}
                  <h3 className="font-medium text-gray-700 mb-3">📋 工作表偵測明細</h3>
                  <div className="space-y-3">
                    {result.detection_log?.map((log, idx) => (
                      <div 
                        key={idx} 
                        className={`p-4 rounded-lg border ${
                          log.apply_discount 
                            ? 'bg-blue-50 border-blue-200' 
                            : 'bg-orange-50 border-orange-200'
                        }`}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center">
                            <Layers className="w-4 h-4 mr-2 text-gray-600" />
                            <span className="font-medium">{log.sheet_name}</span>
                          </div>
                          <div className="flex items-center space-x-2">
                            <span className={`px-2 py-0.5 text-xs rounded ${
                              log.apply_discount 
                                ? 'bg-blue-100 text-blue-700' 
                                : 'bg-orange-100 text-orange-700'
                            }`}>
                              {log.apply_discount ? '✓ 套用折扣' : '✗ 不套用折扣'}
                            </span>
                            <span className="px-2 py-0.5 text-xs rounded bg-gray-100 text-gray-700">
                              {getReportTypeLabel(log.detected_type)}
                            </span>
                          </div>
                        </div>
                        
                        <div className="text-sm text-gray-600">
                          <p>解析 {log.row_count} 筆資料</p>
                          {log.detected_headers && Object.keys(log.detected_headers).length > 0 && (
                            <div className="mt-2">
                              <p className="text-xs text-gray-500 mb-1">偵測欄位對應:</p>
                              <div className="flex flex-wrap gap-1">
                                {Object.entries(log.detected_headers).map(([field, colIdx]) => (
                                  <span key={field} className="px-2 py-0.5 text-xs bg-white rounded border">
                                    {field}: Col {colIdx}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* 金額統計 */}
                <div className="bg-white rounded-lg shadow-lg p-6">
                  <h2 className="text-lg font-semibold mb-4 flex items-center">
                    <BarChart3 className="w-5 h-5 mr-2 text-purple-600" />
                    金額統計
                  </h2>
                  
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="p-4 bg-blue-50 rounded-lg">
                      <p className="text-xs text-blue-600 mb-1">套用折扣金額</p>
                      <p className="text-lg font-bold text-blue-700">
                        {formatCurrency(result.total_amount_with_discount)}
                      </p>
                      {result.calculated?.discount_rate > 0 && result.calculated?.discount_rate < 1 && (
                        <p className="text-sm text-blue-600 mt-1">
                          → {formatCurrency(result.calculated.discounted_part)}
                        </p>
                      )}
                    </div>
                    <div className="p-4 bg-orange-50 rounded-lg">
                      <p className="text-xs text-orange-600 mb-1">不套用折扣金額</p>
                      <p className="text-lg font-bold text-orange-700">
                        {formatCurrency(result.total_amount_no_discount)}
                      </p>
                    </div>
                    <div className="p-4 bg-gray-100 rounded-lg">
                      <p className="text-xs text-gray-600 mb-1">原始總金額</p>
                      <p className="text-lg font-bold text-gray-800">
                        {formatCurrency(result.total_amount)}
                      </p>
                    </div>
                    <div className="p-4 bg-green-50 rounded-lg">
                      <p className="text-xs text-green-600 mb-1">實付金額</p>
                      <p className="text-lg font-bold text-green-700">
                        {formatCurrency(result.calculated?.total_payable || result.total_amount)}
                      </p>
                    </div>
                  </div>
                </div>

                {/* 費用明細 */}
                {result.expenses?.length > 0 && (
                  <div className="bg-white rounded-lg shadow-lg p-6">
                    <h2 className="text-lg font-semibold mb-4 flex items-center">
                      <FileText className="w-5 h-5 mr-2 text-gray-600" />
                      解析明細 ({result.expenses.length} 筆)
                    </h2>
                    
                    <div className="overflow-x-auto max-h-96">
                      <table className="min-w-full divide-y divide-gray-200 text-sm">
                        <thead className="bg-gray-50 sticky top-0">
                          <tr>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">#</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">工作表</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">格式</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">描述</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">類別</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">日期</th>
                            <th className="px-3 py-2 text-right text-xs font-medium text-gray-500">金額</th>
                            <th className="px-3 py-2 text-center text-xs font-medium text-gray-500">折扣</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {result.expenses.slice(0, 100).map((item, index) => (
                            <tr key={index} className={`hover:bg-gray-50 ${!item.apply_discount ? 'bg-orange-50' : ''}`}>
                              <td className="px-3 py-2 text-gray-500">{index + 1}</td>
                              <td className="px-3 py-2">
                                <span className={`px-2 py-0.5 text-xs rounded ${
                                  item.apply_discount 
                                    ? 'bg-blue-100 text-blue-700' 
                                    : 'bg-orange-100 text-orange-700'
                                }`}>
                                  {item.sheet_name || '-'}
                                </span>
                              </td>
                              <td className="px-3 py-2 text-xs text-gray-500">
                                {getReportTypeLabel(item.report_type)}
                              </td>
                              <td className="px-3 py-2 text-gray-900 max-w-xs truncate" title={item.description}>
                                {item.description || '-'}
                              </td>
                              <td className="px-3 py-2 text-gray-600">{item.category || '-'}</td>
                              <td className="px-3 py-2 text-gray-600">{item.date || '-'}</td>
                              <td className="px-3 py-2 text-right font-medium">{formatCurrency(item.amount)}</td>
                              <td className="px-3 py-2 text-center">
                                {item.apply_discount ? (
                                  <CheckCircle className="w-4 h-4 text-green-600 inline" />
                                ) : (
                                  <XCircle className="w-4 h-4 text-orange-600 inline" />
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      {result.expenses.length > 100 && (
                        <p className="text-center text-gray-500 py-2">
                          ... 顯示前 100 筆，共 {result.expenses.length} 筆
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </>
            )}

            {/* 未分析時的提示 */}
            {!result && !analyzing && (
              <div className="bg-white rounded-lg shadow-lg p-12 text-center">
                <Search className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                <h3 className="text-lg font-medium text-gray-600 mb-2">上傳報表開始測試</h3>
                <p className="text-gray-500">
                  系統會自動偵測報表格式，並顯示偵測結果與解析資料
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
